import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { useAuth } from '@/context/AuthContext';
import { apiClient } from '@/api/axios';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Business {
  id: string;
  ownerUserId: string;
  name: string;
  businessTypes: string[];
  status: string;
  logoUrl?: string;
  email?: string;
  phone?: string;
  website?: string;
  description?: string;
  address?: {
    street?: string;
    city?: string;
    customCity?: string;
    state?: string;
    customState?: string;
    zipCode?: string;
    country?: string;
    customCountry?: string;
  };
  openingHours?: Record<string, unknown>;
  taxId?: string;
  vatNumber?: string;
  contactPerson?: {
    name: string;
    role: string;
    phone: string;
    email: string;
  };
  googleBusinessUrl?: string;
  serviceArea?: string;
  licenses?: Array<{ type: string; number: string; expiry: string }>;
  socialMedia?: Record<string, string>;
  onboardingState?: {
    status: string;
    completedSections: Record<string, boolean>;
    lastUpdated?: string;
  };
  holdingOrgId?: string | null;
  holdingOrgName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessMembership {
  business: Business;
  role: string;
  memberId: string;
}

const ACTIVE_BUSINESS_KEY = 'petso_pro_active_business';
export const ALL_BUSINESSES_ID = '__all__' as const;

export interface PendingInviteSummary {
  businessId: string;
  businessName?: string;
  inviteToken?: string | null;
  memberId?: string;
}

interface BusinessContextType {
  // Multi-business state
  memberships: BusinessMembership[];
  activeBusinessId: string | null;
  needsBusinessSelection: boolean;
  isMultiBusiness: boolean;
  isGroupMode: boolean;
  allBusinessIds: string[];

  // Pending invite (if the user has been invited to a business but not yet accepted)
  pendingInvite: PendingInviteSummary | null;
  hasPendingInvite: boolean;

  // Backward-compatible computed properties
  business: Business | null;
  businessId: string | null;
  role: string | null;
  hasBusiness: boolean;

  // Loading / error
  isLoading: boolean;
  isSettingUp: boolean;
  error: string | null;

  // Actions
  setActiveBusiness: (businessId: string) => void;
  deleteBusiness: (businessId: string) => Promise<void>;
  refetch: () => Promise<void>;
  refetchPendingInvite: () => Promise<void>;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

// ─── Provider ────────────────────────────────────────────────────────────────

export function BusinessProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded: isClerkLoaded, getToken } = useClerkAuth();
  const { user: _user } = useAuth();

  const [memberships, setMemberships] = useState<BusinessMembership[]>([]);
  const [activeBusinessId, setActiveBusinessId] = useState<string | null>(null);
  const [needsBusinessSelection, setNeedsBusinessSelection] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSettingUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingInvite, setPendingInvite] =
    useState<PendingInviteSummary | null>(null);

  // Derived state from active membership
  const isGroupMode = activeBusinessId === ALL_BUSINESSES_ID;
  const activeMembership = isGroupMode
    ? null
    : (memberships.find((m) => m.business.id === activeBusinessId) ?? null);
  const business = activeMembership?.business ?? null;
  const role = activeMembership?.role ?? null;
  const hasBusiness = isGroupMode ? memberships.length > 0 : !!business;
  const isMultiBusiness = memberships.length > 1;
  const allBusinessIds = memberships.map((m) => m.business.id);

  // Helper: make an authenticated API call using Clerk's getToken directly.
  const authGet = useCallback(
    async <T,>(url: string): Promise<T> => {
      let token: string | null = null;
      try {
        token = await getToken();
      } catch {
        throw new Error('NO_TOKEN');
      }
      if (!token) throw new Error('NO_TOKEN');
      const { data } = await apiClient.get<T>(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data;
    },
    [getToken]
  );

  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 4;

  // Resolve active business from memberships
  const resolveActiveBusiness = useCallback(
    (newMemberships: BusinessMembership[]) => {
      if (newMemberships.length === 0) {
        setActiveBusinessId(null);
        setNeedsBusinessSelection(false);
        return;
      }

      // 1. Check localStorage for stored selection
      const storedId = localStorage.getItem(ACTIVE_BUSINESS_KEY);
      if (storedId === ALL_BUSINESSES_ID && newMemberships.length > 1) {
        setActiveBusinessId(ALL_BUSINESSES_ID);
        setNeedsBusinessSelection(false);
        return;
      }
      if (storedId && newMemberships.some((m) => m.business.id === storedId)) {
        setActiveBusinessId(storedId);
        setNeedsBusinessSelection(false);
        return;
      }

      // 2. If exactly one membership, auto-select
      if (newMemberships.length === 1) {
        const id = newMemberships[0].business.id;
        setActiveBusinessId(id);
        localStorage.setItem(ACTIVE_BUSINESS_KEY, id);
        setNeedsBusinessSelection(false);
        return;
      }

      // 3. Multiple memberships and no valid stored ID → auto-select first
      const id = newMemberships[0].business.id;
      setActiveBusinessId(id);
      localStorage.setItem(ACTIVE_BUSINESS_KEY, id);
      setNeedsBusinessSelection(false);
    },
    []
  );

  const fetchBusiness = useCallback(async () => {
    if (!isClerkLoaded) return;

    if (!isSignedIn) {
      setMemberships([]);
      setActiveBusinessId(null);
      setNeedsBusinessSelection(false);
      setIsLoading(false);
      retryCountRef.current = 0;
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await authGet<{
        businesses?: BusinessMembership[];
        business: Business | null;
        role: string | null;
        memberId?: string;
      }>('/pro/businesses/mine');

      // Build memberships array — prefer data.businesses, fall back to wrapping single
      let newMemberships: BusinessMembership[];
      if (data.businesses && Array.isArray(data.businesses) && data.businesses.length > 0) {
        newMemberships = data.businesses;
      } else if (data.business) {
        newMemberships = [
          {
            business: data.business,
            role: data.role ?? 'owner',
            memberId: data.memberId ?? '',
          },
        ];
      } else {
        newMemberships = [];
      }

      setMemberships(newMemberships);
      resolveActiveBusiness(newMemberships);
      retryCountRef.current = 0;
    } catch (err: any) {
      if (retryCountRef.current < MAX_RETRIES) {
        const delay = err?.message === 'NO_TOKEN' ? 500 : 1000 * (retryCountRef.current + 1);
        retryCountRef.current += 1;
        retryTimerRef.current = setTimeout(() => fetchBusiness(), delay);
        return;
      }
      console.error('Failed to fetch business:', err);
      setError(err?.response?.data?.error || 'Failed to load business');
      setMemberships([]);
      setActiveBusinessId(null);
    } finally {
      if (retryCountRef.current === 0 || retryCountRef.current >= MAX_RETRIES) {
        setIsLoading(false);
      }
    }
  }, [isSignedIn, isClerkLoaded, authGet, resolveActiveBusiness]);

  useEffect(() => {
    fetchBusiness();
  }, [fetchBusiness]);

  // ── setActiveBusiness ─────────────────────────────────────────────────────
  const setActiveBusiness = useCallback(
    (businessId: string) => {
      if (businessId === ALL_BUSINESSES_ID) {
        localStorage.setItem(ACTIVE_BUSINESS_KEY, ALL_BUSINESSES_ID);
        setActiveBusinessId(ALL_BUSINESSES_ID);
        setNeedsBusinessSelection(false);
        return;
      }
      const exists = memberships.some((m) => m.business.id === businessId);
      if (!exists) return;
      localStorage.setItem(ACTIVE_BUSINESS_KEY, businessId);
      setActiveBusinessId(businessId);
      setNeedsBusinessSelection(false);
    },
    [memberships]
  );

  // ── deleteBusiness ────────────────────────────────────────────────────────
  // Soft-deletes an owned, empty business. Used by the switcher to clean up
  // orphan / auto-created workspaces. Backend refuses if there are members
  // or bookings.
  const deleteBusiness = useCallback(
    async (targetBusinessId: string) => {
      let token: string | null = null;
      try {
        token = await getToken();
      } catch {
        throw new Error('NO_TOKEN');
      }
      if (!token) throw new Error('NO_TOKEN');
      await apiClient.delete(`/pro/businesses/${targetBusinessId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // If the deleted one was active, clear the stored selection so
      // fetchBusiness picks a different one.
      if (activeBusinessId === targetBusinessId) {
        localStorage.removeItem(ACTIVE_BUSINESS_KEY);
        setActiveBusinessId(null);
      }
      await fetchBusiness();
    },
    [getToken, activeBusinessId, fetchBusiness]
  );

  // ── Pending-invite lookup ────────────────────────────────────────────────
  // When the user has zero memberships, we check if they've been invited to a
  // business but haven't accepted yet. We NEVER auto-provision a new business —
  // the user must either accept their invite or explicitly create one through
  // the onboarding flow.
  const refetchPendingInvite = useCallback(async () => {
    if (!isSignedIn) {
      setPendingInvite(null);
      return;
    }
    try {
      const data = await authGet<{
        hasPendingInvite: boolean;
        businessId?: string;
        businessName?: string;
        inviteToken?: string | null;
        memberId?: string;
      }>('/pro/me/pending-invite');
      if (data.hasPendingInvite && data.businessId) {
        setPendingInvite({
          businessId: data.businessId,
          businessName: data.businessName,
          inviteToken: data.inviteToken ?? null,
          memberId: data.memberId,
        });
      } else {
        setPendingInvite(null);
      }
    } catch {
      // Silent — pending-invite endpoint failures shouldn't break the app.
      setPendingInvite(null);
    }
  }, [isSignedIn, authGet]);

  useEffect(() => {
    if (isLoading) return;
    if (!isSignedIn) {
      setPendingInvite(null);
      return;
    }
    if (memberships.length > 0) {
      setPendingInvite(null);
      return;
    }
    refetchPendingInvite();
  }, [isLoading, isSignedIn, memberships.length, refetchPendingInvite]);

  // Clear stored active business on sign-out
  useEffect(() => {
    if (isClerkLoaded && !isSignedIn) {
      localStorage.removeItem(ACTIVE_BUSINESS_KEY);
      setPendingInvite(null);
    }
  }, [isClerkLoaded, isSignedIn]);

  // Clean up retry timer on unmount
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  const value: BusinessContextType = {
    memberships,
    activeBusinessId,
    needsBusinessSelection,
    isMultiBusiness,
    isGroupMode,
    allBusinessIds,
    pendingInvite,
    hasPendingInvite: !!pendingInvite,
    business,
    businessId: activeBusinessId,
    role,
    hasBusiness,
    isLoading: isLoading || isSettingUp,
    isSettingUp,
    error,
    setActiveBusiness,
    deleteBusiness,
    refetch: fetchBusiness,
    refetchPendingInvite,
  };

  return (
    <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useBusiness(): BusinessContextType {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
}
